import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { creamProducts } from "@/data/products";
import { goToShopify, goToShopifyCollection } from "@/lib/shopify";

/**
 * Complete The Look — Classics section in warm taupe.
 */
export const CompleteTheLook = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setIsVisible(true)),
      { threshold: 0.1 }
    );
    const section = document.getElementById("classics-section");
    if (section) observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const set = creamProducts["embossed-cream-unixorn-set"];
  const hoodie = creamProducts["cream-hoodie"];
  const pants = creamProducts["embossed-cream-unixorn-sweatpants"];

  return (
    <section
      id="classics-section"
      data-testid="classics-section"
      className="section-padding bg-[#EBE3D2] relative"
    >
      <div className="container-luxury">
        <div
          className={`text-center mb-16 md:mb-24 transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <p className="text-[11px] tracking-[0.4em] uppercase text-stone-500 mb-5">
            UNIXORN — Classics
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 text-stone-900">
            COMPLETE THE LOOK
          </h2>
          <p className="text-base md:text-lg text-stone-600 tracking-wide max-w-2xl mx-auto">
            One palette. Three pieces. Worn together or apart.
          </p>
        </div>

        {/* Hero Set */}
        <div
          className={`group cursor-pointer max-w-5xl mx-auto mb-16 md:mb-24 transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "150ms" }}
          onClick={() => goToShopify(set.id)}
          data-testid="classics-set-hero"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div className="relative aspect-square overflow-hidden bg-[#F5F0E5] shadow-[0_30px_80px_-30px_rgba(120,100,72,0.3)]">
              <img
                src={set.image}
                alt={set.name}
                className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
              />
            </div>
            <div className="text-center md:text-left">
              <p className="text-[11px] tracking-[0.35em] uppercase text-stone-500 mb-4">
                The Complete Set
              </p>
              <h3 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-stone-900 tracking-tight mb-5">
                {set.name}
              </h3>
              <p className="text-lg md:text-xl text-stone-600 italic mb-7">
                &ldquo;{set.tagline}&rdquo;
              </p>
              <p className="text-stone-700 mb-8 leading-relaxed">
                {set.description}
              </p>
              <p className="text-2xl md:text-3xl text-stone-900 font-semibold mb-10">
                ${set.price}
              </p>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  goToShopify(set.id);
                }}
                className="px-10 py-6 bg-stone-900 text-[#F4EFE6] hover:bg-stone-800 transition-all duration-500 rounded-none uppercase tracking-[0.3em] font-bold text-sm"
                data-testid="classics-set-acquire-btn"
              >
                Acquire The Set
              </Button>
            </div>
          </div>
        </div>

        {/* Individual pieces */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 max-w-4xl mx-auto transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "300ms" }}
        >
          {[hoodie, pants].map((p) => (
            <article
              key={p.id}
              onClick={() => goToShopify(p.id)}
              className="group cursor-pointer"
              data-testid={`classics-piece-${p.id}`}
            >
              <div className="relative aspect-square overflow-hidden bg-[#F5F0E5] mb-6 transition-shadow duration-500 group-hover:shadow-[0_30px_80px_-20px_rgba(120,100,72,0.25)]">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.04]"
                />
              </div>
              <div className="text-center md:text-left">
                <h3 className="text-xl md:text-2xl font-display font-bold text-stone-900 tracking-tight mb-2">
                  {p.name}
                </h3>
                <p className="text-stone-500 text-sm italic mb-3">{p.tagline}</p>
                <p className="text-stone-900 font-semibold">${p.price}</p>
              </div>
            </article>
          ))}
        </div>

        <div className="text-center mt-20">
          <button
            onClick={() => goToShopifyCollection("classics")}
            className="text-stone-600 hover:text-stone-900 transition-colors text-xs tracking-[0.4em] uppercase"
            data-testid="classics-see-all"
          >
            View All Classics →
          </button>
        </div>
      </div>
    </section>
  );
};

export default CompleteTheLook;
